import { useImperativeHandle, useMemo } from "react";
import { forwardRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";

import { comboItemApi } from "src/1/apis/combo_item.api";
import { CModal } from "src/1/common/components/others";
import { useSetQueryData } from "src/1/hooks/query";
import { CCheckbox, CNumberInput } from "src/common/components/controls";
import { CPagination, CTable } from "src/common/components/others";
import { formatSearchString } from "src/utils/funcs";

import { SearchComboItem } from "./Search";

const defaultValues = {
  default_quantity: 1,
};

export const ComboItemModal = forwardRef(({ getAddedData }, ref) => {
  //#region Data
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    mode: "all",
    defaultValues,
  });

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["danh-sach-combo-items"],
    queryFn: () => comboItemApi.getAll(),
    select: (response) =>
      Array.isArray(response) ? response : response?.data?.data,
  });

  const { setQueryData } = useSetQueryData(["danh-sach-combo-items"]);

  const [paginate, setPaginate] = useState({ page: 1, limit: 10 });

  const isSelectAll = useMemo(() => data?.every((e) => e?.check), [data]);

  const [searchValue, setSearchValue] = useState("");

  const visibleData = useMemo(() => {
    if (!searchValue) return data;

    return data?.filter((e) => {
      return formatSearchString(e?.name).includes(
        formatSearchString(searchValue),
      );
    });
  }, [data, searchValue]);
  //#endregion

  //#region Event
  const onClose = () => {
    reset(defaultValues);
    refetch();
    setOpen(false);
  };

  const onSubmit = handleSubmit(async (values) => {
    const payload = data
      ?.filter((e) => e?.check)
      ?.map((e) => ({ ...e, quantity: values?.default_quantity }));
    getAddedData(payload ?? []);

    onClose();
  });

  const onPaginationChange = ({ page, limit }) => {
    setPaginate({ page, limit });
  };

  const onSelect =
    (code = -1) =>
    (value) => {
      setQueryData(
        data.map((e) =>
          code === -1 || e?.code === code ? { ...e, check: value } : e,
        ),
      );
    };

  const onSearch = (value) => {
    setSearchValue(value);
    setPaginate({ page: 1, limit: 10 });
  };
  //#endregion

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }));

  //#region Render
  const fields = [
    {
      key: "select",
      label: <CCheckbox value={isSelectAll} onChange={onSelect()} />,
      sorter: false,
    },
    {
      key: "code",
      label: "Mã combo item",
      _style: { textAlign: "left" },
    },
    {
      key: "name",
      label: "Tên combo item",
      _style: { textAlign: "left" },
    },
    {
      key: "unit",
      label: "Đơn vị tính",
    },
  ];

  const render = {
    select: ({ code, check }) => (
      <td>
        <CCheckbox value={check} onChange={onSelect(code)} />
      </td>
    ),
    code: ({ code }) => <td className="text-left">{code}</td>,
    name: ({ name }) => <td className="text-left">{name}</td>,
  };

  return (
    <CModal
      open={open}
      onClose={onClose}
      title="Thêm combo item"
      onSubmit={onSubmit}
      loading={isSubmitting}
    >
      <div className="flex flex-col">
        <div className="flex flex-row gap-3">
          <Controller
            control={control}
            name="default_quantity"
            render={({ field }) => (
              <CNumberInput
                {...field}
                className="mb-2 max-w-[200px]"
                label="Số lượng"
                required
              />
            )}
          />
          <SearchComboItem onSearch={onSearch} />
        </div>

        <CTable
          className="selectable"
          loading={isFetching}
          fields={fields}
          render={render}
          data={visibleData}
          page={paginate.page}
          itemsPerPage={paginate.limit}
          footer={
            <CPagination
              page={paginate.page}
              total={visibleData?.length}
              limit={paginate.limit}
              onPaginationChange={onPaginationChange}
            />
          }
        />
      </div>
    </CModal>
  );
  //#endregion
});
